import * as React from 'react'
import { DocumentNode, useQuery, useSubscription } from '@apollo/client'
import { InfiniteLoadByOffsetReturn, useInfiniteLoadByOffset } from './infiniteLoadByOffset'
import {
  CollectorLeaderboardOrderBy,
  CreatorLeaderboardOrderBy,
  Query_Root,
  VideoLeaderboardOrderBy,
} from '../graphql/indexer-graphql-types'
import { usePrevious } from './previous'

const PAGE_SIZE = 3

interface infiniteLoadIndexerProps<T> {
  id: string
  query: DocumentNode

  // parse indexer response into raw item array
  parseItems: (value: Query_Root | undefined) => any

  // enrich raw items with firebase
  enrichItems: Function

  page_size?: number

  refetchMarker?: string

  // query or subscription (dynamic) variables
  variables?: {
    [key: string]:
      | string
      | number
      | VideoLeaderboardOrderBy[]
      | CreatorLeaderboardOrderBy[]
      | CollectorLeaderboardOrderBy[]
  }

  // the callback that will be called when new items found from subscription
  subscriptionAdd?: DocumentNode

  // the callback that will be called when items are removed from subscription
  subscriptionsRemove?: DocumentNode[]

  // raw item comparer
  comparer?: Function

  // raw item comparer
  rawToEnrichedComparer?: Function

  // enriched item sorter
  enrichedItemSorter?: (a: T, b: T) => number
}

export function useInfiniteLoadIndexer<T, RawT>({
  id,
  query,
  subscriptionAdd,
  subscriptionsRemove,
  enrichItems,
  comparer,
  parseItems,
  page_size = PAGE_SIZE,
  variables = {},
  refetchMarker,
  enrichedItemSorter,
  rawToEnrichedComparer,
}: infiniteLoadIndexerProps<T>): InfiniteLoadByOffsetReturn<T, RawT> {
  const subscriptionDataAdd =
    subscriptionAdd &&
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useSubscription<Query_Root>(subscriptionAdd, {
      variables: {
        offset: 0,
        limit: page_size,
        ...variables,
      },
    }).data

  const subscriptionDataRemoveBeforeFlat =
    subscriptionsRemove &&
    subscriptionsRemove
      .map(
        (subscriptionRemove) =>
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useSubscription<Query_Root>(subscriptionRemove, {
            variables: {
              offset: 0,
              limit: page_size,
              ...variables,
            },
          }).data
      )
      .map(parseItems)

  const subscriptionDataRemove =
    subscriptionDataRemoveBeforeFlat && subscriptionDataRemoveBeforeFlat.flat()

  const prevSubscriptionDataAdd = usePrevious(subscriptionDataAdd)
  const prevSubscriptionDataRemove = usePrevious(subscriptionDataRemove)

  // we are fully replying on fetchMore to get data
  const { fetchMore } = useQuery<Query_Root>(query, {
    variables: {
      offset: 0,
      limit: 0,
      ...variables,
    },
    skip: true,
  })

  const getItems = React.useCallback(
    async ({
      offset,
      limit,
    }: {
      offset: number
      limit: number
    }): Promise<{ rawData: RawT[]; data: T[]; hasMore: boolean }> => {
      const { data: moreItemsData } = await fetchMore({
        variables: {
          offset,
          limit,
        },
      })
      const originalItems = parseItems(moreItemsData)
      const items = await enrichItems(originalItems)
      const originalLength = originalItems.length
      return { rawData: originalItems, data: items, hasMore: originalLength === limit }
    },
    [enrichItems, fetchMore, parseItems]
  )

  React.useEffect(() => {
    if (!subscriptionAdd || !onSubscriptionDataAdd || !comparer) {
      return
    }

    const subscriptionItems = parseItems(subscriptionDataAdd)
    if (!subscriptionItems?.length) {
      return
    }

    const prevSubscriptionItems = parseItems(prevSubscriptionDataAdd)

    const diffItems = subscriptionItems.filter(
      (item: RawT) => !prevSubscriptionItems.find((itemInList: RawT) => comparer(item, itemInList))
    )

    const newItems = diffItems.filter(
      (item: RawT) => !rawData.find((itemInList) => comparer(item, itemInList))
    )

    if (!newItems.length) {
      return
    }

    ;(async () => {
      try {
        const enricedNewItems = await enrichItems(newItems)
        if (!enricedNewItems.length) {
          return
        }
        onSubscriptionDataAdd(enricedNewItems, newItems)
      } catch (err) {
        console.error(err)
      }
    })()
  }, [subscriptionDataAdd])

  React.useEffect(() => {
    if (
      !subscriptionsRemove?.length ||
      !onSubscriptionDataRemove ||
      !subscriptionDataRemove?.length ||
      !comparer
    ) {
      return
    }

    const diffItems = subscriptionDataRemove.filter(
      (item: RawT) =>
        !prevSubscriptionDataRemove.find((itemInList: RawT) => comparer(item, itemInList))
    )

    const itemsToRemove = diffItems.filter((item: RawT) =>
      rawData.find((itemInList) => comparer(item, itemInList))
    )

    if (!itemsToRemove.length) {
      return
    }

    onSubscriptionDataRemove(itemsToRemove)
  }, [subscriptionDataRemove])

  const {
    rawData,
    data,
    fetchNext,
    hasNext,
    setData,
    error,
    refetch,
    isLoading,
    nextIsLoading,
    isRefetching,
    onSubscriptionDataAdd,
    onSubscriptionDataRemove,
  } = useInfiniteLoadByOffset<T, RawT>({
    id,
    getItems,
    enrichedItemSorter,
    page_size,
    refetchMarker,
    comparer,
    rawToEnrichedComparer,
  })

  return {
    rawData,
    data,
    error,
    setData,
    fetchNext,
    hasNext,
    refetch,
    isLoading,
    nextIsLoading,
    isRefetching,
    onSubscriptionDataRemove,
  }
}
